import React, { useEffect } from 'react';
import { SuccessCelebration } from 'is24-corecss';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCustomerVerificationStatus } from '../../api/tvs/customer/customerVerificationStatus';
import { DSA_DOCUMENT_CHECK_FAILED_PATH, DSA_VERIFICATION_ERROR_PATH } from '../../routePaths';
import { resolveInReviewVerificationRoutePath } from '../verificationLandingPage/verificationRouting';
import { routeWithSearch } from '../../utils/routeWithSearch';

interface DsaNotificationPageProps {
  title?: string;
  description?: string;
  checkCustomerStatus?: boolean;
}

const STATUS_POLL_INTERVAL_MS = 10000;

const DsaNotificationPage: React.FC<DsaNotificationPageProps> = ({
  title,
  description,
  checkCustomerStatus = false,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const notificationTitle = title ?? t('DsaNotificationPage.title');
  const notificationDescription = description ?? t('DsaNotificationPage.description');

  useEffect(() => {
    if (!checkCustomerStatus) {
      return undefined;
    }

    let isMounted = true;
    let timeoutId: number | undefined;

    const checkVerificationStatus = async () => {
      try {
        const verificationStatus = await getCustomerVerificationStatus();
        const routePath = resolveInReviewVerificationRoutePath(verificationStatus);

        if (isMounted && routePath !== location.pathname) {
          if (routePath === DSA_DOCUMENT_CHECK_FAILED_PATH && verificationStatus.rejectionReason) {
            navigate(routeWithSearch(DSA_DOCUMENT_CHECK_FAILED_PATH, location.search), {
              replace: true,
              state: { rejectionReason: verificationStatus.rejectionReason },
            });
            return;
          }

          navigate(routeWithSearch(routePath, location.search), { replace: true });
        }
      } catch {
        if (isMounted) {
          navigate(routeWithSearch(DSA_VERIFICATION_ERROR_PATH, location.search), { replace: true });
        }
      } finally {
        if (isMounted) {
          timeoutId = window.setTimeout(checkVerificationStatus, STATUS_POLL_INTERVAL_MS);
        }
      }
    };

    void checkVerificationStatus();

    return () => {
      isMounted = false;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [checkCustomerStatus, location.pathname, location.search, navigate]);

  return (
    <div
      className='one-whole display-flex grid-justify-center grid-align-center align-center margin-vertical-xxl'
      style={{ flex: '1 0 auto' }}
    >
      <SuccessCelebration.Root variant='animated'>
        <SuccessCelebration.Icon />
        <SuccessCelebration.Title>{notificationTitle}</SuccessCelebration.Title>
        <SuccessCelebration.Description>{notificationDescription}</SuccessCelebration.Description>
      </SuccessCelebration.Root>
    </div>
  );
};

export default DsaNotificationPage;
